import { Component, EventEmitter, Input, Output, NgZone, OnChanges, SimpleChanges, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { ChatTranslations } from '../../../services/chat-translation.service';
import { Message, ToolCall } from '../../../services/wings-assistant-chat.service';

@Component({
  selector: 'wings-message',
  standalone: false,
  templateUrl: './wings-message.component.html',
  styleUrls: ['./wings-message.component.scss'],
  // Usar a estratégia de detecção de mudanças OnPush para melhor desempenho
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WingsMessageComponent implements OnChanges {
  @Input() message: Message;
  @Input() index: number;
  @Input() isLoading: boolean = false;
  @Input() isSystemMessage: boolean = false;
  @Input() translations: ChatTranslations;
  @Input() showToolCalls: boolean = false; // Mostrar accordion de tool calls apenas para usuários autorizados
  
  @Output() feedbackRequested = new EventEmitter<{message: Message, index: number}>();
  
  // Para rastrear o estado do feedback localmente
  private _feedbackState: boolean = false;
  
  constructor(
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}
  
  public get isUserMessage(): boolean {
    return this.message && this.message.role === 'user';
  }
  
  public get isAssistantMessage(): boolean {
    return this.message && this.message.role === 'assistant';
  }
  
  public get hasToolCalls(): boolean {
    return this.message && this.message.toolCalls && this.message.toolCalls.length > 0;
  }
  
  public get hasError(): boolean {
    if (!this.message) return false;
    if (this.message.error) return true;
    if (this.message.toolCalls && this.message.toolCalls.length > 0) {
      return this.message.toolCalls.some(tc => tc.status === 'error');
    }
    return false;
  }
  
  public toggleToolCall(toolCall: ToolCall): void {
    if (!this.showToolCalls) return;
    toolCall.isExpanded = !toolCall.isExpanded;
    this.cdr.markForCheck();
  }
  
  public formatToolArgs(args: any): string {
    try {
      return JSON.stringify(args, null, 2);
    } catch {
      return String(args);
    }
  }
  
  public formatToolResult(result: any): string {
    try {
      if (typeof result === 'string') {
        return result;
      }
      return JSON.stringify(result, null, 2);
    } catch {
      return String(result);
    }
  }
  
  @Input() feedbackIndexes: number[] = []; // Receber o array de índices com feedback

  public get hasFeedback(): boolean {
    // Verificar de três formas diferentes para maior robustez:
    // 1. Se a mensagem tem um objeto de feedback anexado
    // 2. Se a mensagem tem a flag feedbackDone marcada como true
    // 3. Se o índice atual da mensagem está na lista de feedbacks enviados
    return !!(this.message && this.message.feedback) || 
           !!(this.message && this.message.feedbackDone) || 
           (Array.isArray(this.feedbackIndexes) && this.index >= 0 && this.feedbackIndexes.includes(this.index));
  }
  
  public requestFeedback(): void {
    this.feedbackRequested.emit({ message: this.message, index: this.index });
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    // Atualizar o estado do feedback sempre que houver mudanças relevantes
    if (changes.message || changes.feedbackIndexes || changes.index) {
      this.updateFeedbackState();
    }
    
    // Sempre forçar detecção de mudanças quando message muda
    // Importante para OnPush strategy com propriedades internas como isStreaming
    if (changes.message) {
      this.cdr.detectChanges();
    }
  }
  
  private updateFeedbackState(): void {
    // Verificar o estado do feedback e armazená-lo localmente
    this._feedbackState = this.calculateHasFeedback();
    
    // Sempre que atualizarmos o estado, forçar detecção de mudanças
    this.cdr.markForCheck();
  }
  
  private calculateHasFeedback(): boolean {
    // Verificação robusta para diversas condições
    if (!this.message) return false;
    
    // Verificar explicitamente a propriedade feedback
    if (this.message.feedback) return true;
    
    // Verificar a flag feedbackDone
    if (this.message.feedbackDone === true) return true;
    
    // Verificar no array de índices
    if (Array.isArray(this.feedbackIndexes) && 
        typeof this.index === 'number' && 
        this.index >= 0 && 
        this.feedbackIndexes.includes(this.index)) {
      return true;
    }
    
    // Nenhuma condição atendida, não há feedback
    return false;
  }
}